import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ResponseArtifactDTO,
  UpdateResponseArtifactRequest,
  UpdateResponseArtifactResponse,
} from '@dreamai/types';
import api from '../../api';
import ResponseArtifactModal from './sidebar/ResponseArtifactModal';
import InlineMultipleChoice from './InlineMultipleChoice';
import type { CardSelection } from './sidebar/SpendingApprovalModal';
import { ReactComponent as HourglassIcon } from '../../icons/hourglass.svg';
import { ReactComponent as CheckSmallIcon } from '../../icons/check-small.svg';
import { ReactComponent as DocumentTextIcon } from '../../icons/document-text.svg';
import './task-escalation-card.scss';
import './inline-response-artifact.scss';

/**
 * Inline card rendered in ThreadView when the brancher calls
 * `showResponseArtifact`. Styled to match `TaskStartedPane` —
 * shows an icon frame, title, and the appropriate CTA:
 *   - `multiple_choice` renders `InlineMultipleChoice` directly
 *   - All other interactive types open `ResponseArtifactModal`
 *   - `information_dump` with projectAppId navigates to research
 */
interface InlineResponseArtifactProps {
  artifactId: string;
  /** Parent thread ID — used for research document navigation. */
  threadId: string;
}

const MODAL_FRIENDLY_TYPES = new Set([
  'multiple_choice',
  'yes_or_no',
  'form_input',
  'spending_approval',
  'confirmation_query',
]);

const getOpenLabel = (artifact: ResponseArtifactDTO): string => {
  const type = artifact.artifactType as string;
  if (type === 'form_input')
    return artifact.confirmationId ? 'Open approval' : 'Open form';
  if (type === 'spending_approval') return 'Open approval';
  if (type === 'multiple_choice') return 'Open options';
  if (type === 'yes_or_no') return 'Open question';
  if (type === 'confirmation_query') return 'Open question';
  if (type === 'information_dump') return 'Open research';
  return 'Review and respond';
};

const InlineResponseArtifact: React.FC<InlineResponseArtifactProps> = ({
  artifactId,
  threadId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [, setSearchParams] = useSearchParams();
  const [artifact, setArtifact] = useState<ResponseArtifactDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifactId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data.success) setArtifact(res.data.artifact);
      } catch (err) {
        console.error('InlineResponseArtifact: fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [artifactId, getAccessTokenSilently]);

  const handleSubmitResponse = useCallback(
    async (
      art: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
        }>;
      },
      cardSelection?: CardSelection,
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: art.id,
          status: 'actioned',
          userResponse: response,
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${art.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setArtifact((prev) =>
          prev ? { ...prev, status: 'actioned', userResponse: response } : prev,
        );
        setModalOpen(false);
      } catch (err) {
        console.error('InlineResponseArtifact: submit failed', err);
      }
    },
    [getAccessTokenSilently],
  );

  if (loading) {
    return (
      <div className='inline-response-artifact'>
        <div
          className='task-status-pane status-started'
          style={{ minHeight: 168 }}
        />
      </div>
    );
  }

  if (!artifact) return null;

  const type = artifact.artifactType as string;
  const isActioned = artifact.status === 'actioned';
  const isDone = isActioned;

  // Multiple choice renders inline — cream wrapper with header
  if (type === 'multiple_choice') {
    return (
      <div className='inline-response-artifact inline-response-artifact--mc'>
        <div className='ira-mc-header'>
          <span className='ira-mc-header-label'>Task complete</span>
        </div>
        <InlineMultipleChoice artifactId={artifactId} threadId={threadId} />
      </div>
    );
  }

  // Resolve projectAppId for information_dump research navigation
  const projectAppId =
    artifact.projectAppId ||
    artifact.linkedArtifacts?.find((la) => la.projectAppId)?.projectAppId ||
    undefined;

  // Information dump renders as a compact pill link
  if (type === 'information_dump' && projectAppId) {
    return (
      <div className='inline-response-artifact'>
        <button
          type='button'
          className='ira-research-pill'
          onClick={() =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set('app_tab', 'documents');
                next.set('research_app_id', projectAppId);
                return next;
              },
              { replace: true },
            )
          }
        >
          <DocumentTextIcon className='ira-research-pill-icon' />
          <span>{artifact.title || 'Research document'}</span>
        </button>
      </div>
    );
  }

  const title = artifact.title || 'Action required';
  const subtitle = isDone ? 'Completed' : 'Waiting for your response';

  const isModalFriendly = MODAL_FRIENDLY_TYPES.has(type);

  return (
    <div className='inline-response-artifact'>
      <div className='task-status-pane status-started'>
        <span className='status-icon-frame'>
          {isDone ? (
            <CheckSmallIcon className='status-step-icon status-complete' />
          ) : (
            <HourglassIcon className='status-step-icon status-waiting' />
          )}
        </span>

        <h3 className='status-title'>{title}</h3>
        <p className='status-subtitle'>{subtitle}</p>

        {/* Modal-friendly CTA */}
        {isModalFriendly && type !== 'multiple_choice' && (
          <div className='status-artifact-opener'>
            <button
              type='button'
              className='confirm-btn'
              disabled={isDone}
              onClick={() => setModalOpen(true)}
            >
              {isDone ? '✓ Done' : getOpenLabel(artifact)}
            </button>
          </div>
        )}

        {/* Non-modal, non-research: full-screen navigation */}
        {!isModalFriendly && !projectAppId && !isDone && (
          <div className='status-artifact-opener'>
            <button
              type='button'
              className='confirm-btn'
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('app_tab', 'documents');
                    return next;
                  },
                  { replace: true },
                )
              }
            >
              {getOpenLabel(artifact)}
            </button>
          </div>
        )}
      </div>

      {/* Modal for interactive artifact types */}
      {isModalFriendly && (
        <ResponseArtifactModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          artifacts={[artifact]}
          initialIndex={0}
          onSubmitResponse={handleSubmitResponse}
          readOnly={isDone}
        />
      )}
    </div>
  );
};

export default InlineResponseArtifact;
